import * as DialogPrimitive from "@radix-ui/react-dialog";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import React, { useEffect, useRef, useState } from "react";

import { OptionsModalType } from "../../@types/Modal";
import { Close } from "../../assets/icons/Close";
import { CustomIconButton } from "../CustomIconButton";
import {
  StyledDialogClose,
  StyledDialogContent,
  StyledDialogDescription,
  StyledDialogOverlay,
  StyledDialogTitle,
  StyledTile,
} from "./Modal.styles";

type ModalProps = React.ComponentProps<typeof DialogPrimitive.Root> &
  DialogPrimitive.DialogContentProps & {
    optionsModal: OptionsModalType;
    sidePanel?: React.ReactNode;
    sidePanelOptions?: OptionsModalType;
    // Render the dialog in a portal at document.body. Opt-in: when a Modal is
    // mounted deep inside a styled subtree (e.g. a patient-details card), the
    // surrounding CSS would otherwise be inherited and break its layout.
    portal?: boolean;
  };

export function Modal({
  children,
  optionsModal,
  sidePanel,
  sidePanelOptions,
  portal = false,
  ...props
}: ModalProps) {
  const [hasTabed, setHasTabed] = useState(false);
  const modalRef = useRef(null);

  useEffect(() => {
    const handleTabTrue = (event: any) => {
      if (event.key === "Tab" && !hasTabed) {
        setHasTabed(true);
      }
    };
    if (props.open && modalRef.current) {
      (modalRef.current as HTMLElement).focus();
    }

    document.addEventListener("keydown", handleTabTrue, true);
    return () => {
      setHasTabed(false);
    };
  }, [props.open]);

  // Escape and outside clicks close through onOpenChange; a non-dismissible
  // modal swallows both so only its own buttons can close it.
  const blockDismiss =
    optionsModal.dismissible === false
      ? (event: Event) => event.preventDefault()
      : undefined;

  const overlay = (
    <StyledDialogOverlay ref={modalRef}>
      <VisuallyHidden.Root>
        <StyledDialogTitle aria-describedby={undefined}>
          <StyledDialogDescription />
        </StyledDialogTitle>
      </VisuallyHidden.Root>

      <StyledDialogContent
        onEscapeKeyDown={blockDismiss}
        onPointerDownOutside={blockDismiss}
        onInteractOutside={blockDismiss}
      >
        <StyledTile
          css={{
            width: optionsModal.width || "925px",
            maxWidth: optionsModal.maxWidth || undefined,
            height: optionsModal.height || "auto",
            minHeight: optionsModal.minHeight || undefined,
            maxHeight: optionsModal.maxHeight || undefined,
            padding: optionsModal.padding || "16px",
          }}
        >
          {optionsModal.showCloseButton ? (
            <StyledDialogClose outline={hasTabed}>
              <CustomIconButton>
                <Close />
              </CustomIconButton>
            </StyledDialogClose>
          ) : null}

          {children}
        </StyledTile>

        {sidePanel ? (
          <StyledTile
            css={{
              width: sidePanelOptions?.width || "640px",
              maxWidth: sidePanelOptions?.maxWidth || undefined,
              height: sidePanelOptions?.height || optionsModal.height || "auto",
              minHeight: sidePanelOptions?.minHeight || undefined,
              maxHeight: sidePanelOptions?.maxHeight || undefined,
              padding: sidePanelOptions?.padding || "16px",
            }}
          >
            {sidePanel}
          </StyledTile>
        ) : null}
      </StyledDialogContent>
    </StyledDialogOverlay>
  );

  return (
    <DialogPrimitive.Root {...props}>
      {portal ? (
        <DialogPrimitive.Portal>{overlay}</DialogPrimitive.Portal>
      ) : (
        overlay
      )}
    </DialogPrimitive.Root>
  );
}
